// FECHAMENTO-CC Fase 2 (2026-08-17): alocação padrão de mão de obra.
//
// A gerência declara, por pessoa, em quais contratos ela costuma trabalhar
// (% com vigência). Isso preenche SÓ as horas não apontadas no Clockify —
// quando a pessoa anota mais, o preenchimento encolhe sozinho.
//
// A tela mostra primeiro a COBERTURA do mês (quanto cada um apontou em
// contrato × carga) — é o que diz quem precisa de alocação padrão.
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, App as AntApp, Button, DatePicker, Drawer, Form, InputNumber, Popconfirm, Progress,
  Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { usePerms } from '../../hooks/usePerms';

const { Text } = Typography;

async function api(path: string, opts?: RequestInit) {
  const token = await getToken();
  const r = await fetch(`${API_URL}${path}`, {
    ...opts, headers: { Authorization: token, 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  let d: any = await r.json().catch(() => ({}));
  if (d && typeof d === 'object' && 'body' in d && typeof d.body === 'string') { try { d = JSON.parse(d.body); } catch {} }
  if (!r.ok || d?.erro) throw new Error(d?.erro || `Erro ${r.status}`);
  return d;
}

type Pessoa = {
  id: string; nome_canonical: string; horas_dia: string; coeficiente: string; clockify_user_id: string | null;
  h_total: number; h_contrato: number; pct_padrao: string;
  alocacoes: { contrato_id: string; numero: string; percentual: string }[] | null;
};
type Aloc = {
  id: string; profissional_id: string; profissional_nome: string; contrato_id: string; contrato_numero: string;
  contrato_titulo: string; cliente: string; percentual: string; vigencia_inicio: string; vigencia_fim: string | null; motivo: string | null;
};

export default function AlocacaoPadrao() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode('financeiro.alocacao_padrao.editar') || pode('admin.permissoes.editar');
  const [comp, setComp] = useState<Dayjs>(dayjs().subtract(1, 'month'));
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [alocs, setAlocs] = useState<Aloc[]>([]);
  const [contratos, setContratos] = useState<{ id: string; numero: string; titulo: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [aberto, setAberto] = useState<Pessoa | null>(null);
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api(`/financeiro/alocacao-padrao/cobertura?competencia=${comp.format('YYYY/MM')}`),
        api('/financeiro/alocacao-padrao?vigente=1'),
      ]);
      setPessoas(c.pessoas || []); setAlocs(a.alocacoes || []);
    } catch (e: any) { message.error(e.message); }
    finally { setLoading(false); }
  }, [comp, message]);
  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    // contratos (pai) para o seletor — mesma action da lista do operacional
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_URL}/comercial/propostas-geradas`, {
          method: 'POST', headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'op_listar_contratos' }),
        });
        let d: any = await r.json().catch(() => []);
        if (d && d.body) { try { d = JSON.parse(d.body); } catch {} }
        const lista = Array.isArray(d) ? d : [];
        setContratos(lista
          .filter((x: any) => !x.contrato_pai_id && !['cancelado', 'encerrado', 'concluido'].includes(String(x.status || '')))
          .map((x: any) => ({ id: x.id, numero: x.numero, titulo: x.titulo || x.cliente_nome || '' })));
      } catch { setContratos([]); }
    })();
  }, []);

  async function salvar() {
    if (!aberto) return;
    const v = await form.validateFields();
    setSalvando(true);
    try {
      await api('/financeiro/alocacao-padrao', { method: 'POST', body: JSON.stringify({
        profissional_id: aberto.id, contrato_id: v.contrato_id, percentual: v.percentual,
        vigencia_inicio: v.vigencia_inicio.format('YYYY-MM-DD'), vigencia_fim: v.vigencia_fim ? v.vigencia_fim.format('YYYY-MM-DD') : null,
        motivo: v.motivo || null,
      }) });
      message.success('Alocação salva — vale a partir do próximo recálculo (cron diário ou botão Recalcular).');
      form.resetFields(); await carregar();
    } catch (e: any) { message.error(e.message, 8); }
    finally { setSalvando(false); }
  }

  async function encerrar(id: string) {
    try { await api(`/financeiro/alocacao-padrao/${id}`, { method: 'DELETE' }); message.success('Encerrada'); await carregar(); }
    catch (e: any) { message.error(e.message); }
  }

  const du = 21; // aproximação para a barra; o motor usa dias úteis reais
  return (
    <div style={{ padding: 16 }}>
      <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Mão de obra por contrato — cobertura e alocação padrão</Typography.Title>
        <DatePicker picker="month" value={comp} onChange={(v) => v && setComp(v)} format="MM/YYYY" allowClear={false} />
      </Space>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="O custo de cada pessoa cai nos contratos proporcional às horas apontadas no Clockify. O que não foi apontado em contrato é preenchido pela alocação padrão que você declara aqui; o resto vira overhead."
        description="R$/h = nota do mês × coeficiente de encargos ÷ max(carga mensal, horas apontadas). Quem aponta pouco em contrato precisa de alocação padrão — a barra mostra." />

      <Table size="small" rowKey="id" loading={loading} pagination={false} dataSource={pessoas}
        columns={[
          { title: 'Pessoa', dataIndex: 'nome_canonical', width: 220, ellipsis: true },
          { title: 'Apontou no mês', key: 'h', width: 260, render: (_: any, p: Pessoa) => {
            const carga = du * (parseFloat(p.horas_dia) || 8);
            const pctCtr = carga ? Math.min(100, p.h_contrato / carga * 100) : 0;
            const pctTot = carga ? Math.min(100, p.h_total / carga * 100) : 0;
            return (
              <Tooltip title={`${p.h_contrato.toFixed(0)} h em contrato de ${p.h_total.toFixed(0)} h apontadas (carga ~${carga} h)`}>
                <div style={{ minWidth: 0 }}>
                  <Progress percent={Math.round(pctTot)} success={{ percent: Math.round(pctCtr) }} size="small" showInfo={false} />
                  <Text type="secondary" style={{ fontSize: 11 }}>{p.h_contrato.toFixed(0)} h em contrato · {p.h_total.toFixed(0)} h total</Text>
                </div>
              </Tooltip>
            );
          } },
          { title: 'Alocação padrão vigente', key: 'a', render: (_: any, p: Pessoa) => {
            const soma = parseFloat(p.pct_padrao) || 0;
            if (!p.alocacoes?.length) return <Text type="secondary" style={{ fontSize: 12 }}>nenhuma — horas não apontadas viram overhead</Text>;
            return (
              <Space wrap size={4}>
                {p.alocacoes.map(a => <Tag key={a.contrato_id}>{a.numero} · {parseFloat(a.percentual).toFixed(0)}%</Tag>)}
                {soma < 100 && <Text type="secondary" style={{ fontSize: 11 }}>({(100 - soma).toFixed(0)}% overhead)</Text>}
              </Space>
            );
          } },
          { title: 'Coef.', dataIndex: 'coeficiente', width: 70, align: 'right' as const, render: (v: string) => parseFloat(v).toFixed(2) },
          { title: '', key: 'x', width: 110, align: 'right' as const, render: (_: any, p: Pessoa) => (
            <Button size="small" icon={<PlusOutlined />} disabled={!podeEditar} onClick={() => { setAberto(p); form.resetFields(); }}>Alocar</Button>
          ) },
        ]} />

      <Drawer open={!!aberto} onClose={() => setAberto(null)} width="min(720px, 96vw)"
        title={aberto ? `Alocação padrão — ${aberto.nome_canonical}` : ''}>
        {aberto && (
          <>
            <Table size="small" rowKey="id" pagination={false} style={{ marginBottom: 16 }}
              dataSource={alocs.filter(a => a.profissional_id === aberto.id)}
              locale={{ emptyText: 'Nenhuma alocação vigente' }}
              columns={[
                { title: 'Contrato', key: 'c', ellipsis: true, render: (_: any, a: Aloc) => `${a.contrato_numero} · ${a.contrato_titulo}` },
                { title: '%', dataIndex: 'percentual', width: 60, align: 'right' as const, render: (v: string) => parseFloat(v).toFixed(0) },
                { title: 'Vigência', key: 'v', width: 170, render: (_: any, a: Aloc) => `${String(a.vigencia_inicio).slice(0, 10)} → ${a.vigencia_fim ? String(a.vigencia_fim).slice(0, 10) : 'aberta'}` },
                { title: '', key: 'x', width: 50, render: (_: any, a: Aloc) => (
                  <Popconfirm title="Encerrar esta alocação?" onConfirm={() => encerrar(a.id)} okText="Encerrar" cancelText="Voltar" disabled={!podeEditar}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={!podeEditar} />
                  </Popconfirm>
                ) },
              ]} />
            <Form form={form} layout="vertical" initialValues={{ vigencia_inicio: dayjs().startOf('month') }}>
              <Form.Item name="contrato_id" label="Contrato" rules={[{ required: true }]}>
                <Select showSearch placeholder="Contrato (pai)" optionFilterProp="label"
                  options={contratos.map(c => ({ value: c.id, label: `${c.numero} · ${c.titulo}` }))} />
              </Form.Item>
              <Space wrap>
                <Form.Item name="percentual" label="% do tempo" rules={[{ required: true }]}>
                  <InputNumber min={1} max={100} style={{ width: 110 }} addonAfter="%" />
                </Form.Item>
                <Form.Item name="vigencia_inicio" label="A partir de" rules={[{ required: true }]}>
                  <DatePicker format="DD/MM/YYYY" />
                </Form.Item>
                <Form.Item name="vigencia_fim" label="Até (opcional)">
                  <DatePicker format="DD/MM/YYYY" />
                </Form.Item>
              </Space>
              <Form.Item name="motivo" label="Observação">
                <Select mode="tags" placeholder="ex.: coordena o campo deste contrato" open={false} />
              </Form.Item>
              <Button type="primary" loading={salvando} onClick={salvar} disabled={!podeEditar}>Salvar alocação</Button>
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 12 }}>
                A soma das alocações da pessoa não pode passar de 100%. Preenche só as horas não apontadas.
              </Text>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  );
}
